import { useState, type FormEvent, type ReactNode } from "react";
import { createAuthClient, type AuthClient } from "@pustak-lms/api-client";
import { BrandLogo } from "./brand";

const sharedClient = createAuthClient();
export function PasswordPage({ kind, surface, client = sharedClient, embedded = false, footer }: {
  kind: "forgot" | "reset" | "change";
  surface: "platform" | "manage" | "learn";
  client?: AuthClient;
  embedded?: boolean;
  footer?: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "");
  const prefix = surface === "platform" ? "" : `/${surface}`;
  const title = kind === "forgot" ? "Forgot password" : kind === "reset" ? "Reset password" : "Change password";
  const Container = embedded ? "div" : "main";
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    const form = event.currentTarget;
    const data = new FormData(form);
    const next = String(data.get("newPassword") ?? "");
    const confirm = String(data.get("confirmPassword") ?? "");
    if (kind !== "forgot" && next !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      if (kind === "forgot") await client.forgotPassword(String(data.get("email")), surface);
      else if (kind === "reset") await client.resetPassword(token, next, confirm);
      else await client.changePassword(String(data.get("currentPassword")), next, confirm);
      form.reset(); setDone(true);
      if (kind === "reset") window.history.replaceState(null, "", window.location.pathname);
    } catch {
      setError(kind === "reset" ? "Reset link is invalid or expired, or requests are temporarily limited. Request a new link or try again later." : kind === "change" ? "Unable to change password. Check your current password and session, or try again later." : "Unable to request a reset right now. Please wait a minute and try again.");
    } finally { setBusy(false); }
  }
  return <Container className={embedded ? "auth-screen auth-screen-embedded" : "auth-screen"}><section className="auth-card">
    <BrandLogo className="auth-brand" /><h1>{title}</h1>
    {done ? <p role="status">{kind === "forgot" ? "If an account exists for this email, password reset instructions have been sent." : kind === "reset" ? "Password reset. Sign in with your new password." : "Password changed. Your other sessions have been signed out."}</p> : kind === "reset" && !token ? <p role="alert">Reset link is missing. Request a new link.</p> : <form onSubmit={(event) => void submit(event)}>
      {kind === "forgot" ? <><label htmlFor="recovery-email">Email</label><input id="recovery-email" name="email" type="email" autoComplete="username" required disabled={busy} /></> : <>
        {kind === "change" ? <><label htmlFor="current-password">Current password</label><input id="current-password" name="currentPassword" type="password" autoComplete="current-password" required maxLength={128} disabled={busy} /></> : null}
        <label htmlFor="new-password">New password</label><input id="new-password" name="newPassword" type="password" autoComplete="new-password" required minLength={12} maxLength={128} disabled={busy} />
        <label htmlFor="confirm-password">Confirm password</label><input id="confirm-password" name="confirmPassword" type="password" autoComplete="new-password" required minLength={12} maxLength={128} disabled={busy} />
        <p>Use 12–128 characters.</p>
      </>}
      {error ? <p role="alert" className="auth-error">{error}</p> : null}
      <button type="submit" className="button button-primary" disabled={busy}>{busy ? "Please wait…" : kind === "forgot" ? "Send reset instructions" : title}</button>
    </form>}
    {kind === "reset" ? <a href={`${prefix}/forgot-password`}>Request a new reset link</a> : null}
    <a href={kind === "change" ? prefix || "/" : `${prefix}/login`}>{kind === "change" ? "Back to workspace" : "Back to sign in"}</a>
    {footer}
  </section></Container>;
}
